<template>
  <div class="pm-toolbar" role="toolbar" aria-label="Editor toolbar">
    <!-- Block format dropdown -->
    <button
      type="button"
      class="pm-toolbar__block"
      :title="'Paragraph style'"
      @mousedown.prevent
      @click="toggleBlockMenu"
      ref="blockBtnEl"
    >
      <span>{{ currentBlockLabel }}</span>
      <i class="pi pi-chevron-down pm-toolbar__chev" />
    </button>
    <Teleport to="body">
      <div
        v-if="blockMenuOpen"
        class="pm-toolbar__menu"
        :style="blockMenuStyle"
        @mousedown.stop
      >
        <button
          v-for="opt in blockOptions"
          :key="opt.value"
          type="button"
          class="pm-toolbar__menu-item"
          :class="{ 'is-active': currentBlockValue === opt.value }"
          @click="applyBlock(opt.value); blockMenuOpen = false"
        >
          <span :class="opt.previewClass">{{ opt.label }}</span>
        </button>
      </div>
    </Teleport>

    <span class="pm-toolbar__sep" />

    <!-- Undo / redo -->
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Undo (Ctrl+Z)"
      :disabled="!canUndo"
      @mousedown.prevent
      @click="doUndo"
    >
      <i class="pi pi-undo" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Redo (Ctrl+Shift+Z)"
      :disabled="!canRedo"
      @mousedown.prevent
      @click="doRedo"
    >
      <i class="pi pi-refresh" />
    </button>

    <span class="pm-toolbar__sep" />

    <!-- Inline marks -->
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Bold (Ctrl+B)"
      :class="{ 'is-active': isMarkActive('strong') || isMarkActive('bold') }"
      @mousedown.prevent
      @click="toggleMarkByName(boldOrStrongName)"
    >
      <strong class="pm-toolbar__label">B</strong>
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Italic (Ctrl+I)"
      :class="{ 'is-active': isMarkActive('em') || isMarkActive('italic') }"
      @mousedown.prevent
      @click="toggleMarkByName(emOrItalicName)"
    >
      <em class="pm-toolbar__label">I</em>
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Underline (Ctrl+U)"
      :class="{ 'is-active': isMarkActive('underline') }"
      @mousedown.prevent
      @click="toggleMarkByName('underline')"
    >
      <span class="pm-toolbar__label pm-toolbar__label--u">U</span>
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Strikethrough"
      :class="{ 'is-active': isMarkActive('strike') }"
      @mousedown.prevent
      @click="toggleMarkByName('strike')"
    >
      <s class="pm-toolbar__label">S</s>
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Highlight"
      :class="{ 'is-active': isMarkActive('highlight') }"
      @mousedown.prevent
      @click="toggleMarkByName('highlight')"
    >
      <i class="pi pi-pencil" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Inline code"
      :class="{ 'is-active': isMarkActive('code') }"
      @mousedown.prevent
      @click="toggleMarkByName('code')"
    >
      <i class="pi pi-code" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Link (Ctrl+K)"
      :class="{ 'is-active': isMarkActive('link') }"
      @mousedown.prevent
      @click="setLink"
    >
      <i class="pi pi-link" />
    </button>

    <span class="pm-toolbar__sep" />

    <!-- Lists -->
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Bullet list"
      :class="{ 'is-active': isInList('bullet_list') }"
      @mousedown.prevent
      @click="wrapList('bullet_list')"
    >
      <i class="pi pi-list" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Numbered list"
      :class="{ 'is-active': isInList('ordered_list') }"
      @mousedown.prevent
      @click="wrapList('ordered_list')"
    >
      <span class="pm-toolbar__ol">1.</span>
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Task list"
      :class="{ 'is-active': isInList('task_list') }"
      @mousedown.prevent
      @click="wrapList('task_list')"
    >
      <i class="pi pi-check-square" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Indent (Tab)"
      @mousedown.prevent
      @click="indent"
    >
      <i class="pi pi-angle-double-right" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Outdent (Shift+Tab)"
      @mousedown.prevent
      @click="outdent"
    >
      <i class="pi pi-angle-double-left" />
    </button>

    <span class="pm-toolbar__sep" />

    <!-- Insertions -->
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Insert table"
      @mousedown.prevent
      @click="insertTable"
    >
      <i class="pi pi-table" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Insert image"
      @mousedown.prevent
      @click="insertImage"
    >
      <i class="pi pi-image" />
    </button>
    <button
      type="button"
      class="pm-toolbar__btn"
      title="Insert divider"
      @mousedown.prevent
      @click="insertDivider"
    >
      <i class="pi pi-minus" />
    </button>

    <!-- Spacer pushes the print to the right -->
    <span class="pm-toolbar__flex" />

    <button
      type="button"
      class="pm-toolbar__btn"
      title="Print"
      @mousedown.prevent
      @click="doPrint"
    >
      <i class="pi pi-print" />
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * Persistent rich-text toolbar for the ProseMirror editor.
 *
 * Receives the live EditorView and EditorState; computes active states
 * (mark / block / list) for the current selection. All commands use
 * v.dispatch(...) so transactions go through the wrapper's dispatchTransaction
 * (autosave, modelValue emit, etc. keep working).
 *
 * Buttons use @mousedown.prevent so the editor doesn't lose its selection
 * when the user clicks a button — important for "toggle bold on selected text".
 */
import { ref, computed, type PropType } from 'vue';
import type { EditorView } from 'prosemirror-view';
import type { EditorState, Transaction } from 'prosemirror-state';
import { Selection, TextSelection } from 'prosemirror-state';
import type { NodeType, MarkType } from 'prosemirror-model';
import { toggleMark, setBlockType, wrapIn, lift } from 'prosemirror-commands';
import { wrapInList, liftListItem, sinkListItem } from 'prosemirror-schema-list';
import { undo, redo, undoDepth, redoDepth } from 'prosemirror-history';
import { pmSchema } from './pmSchema';

const props = defineProps({
  view: { type: Object as PropType<EditorView>, required: true },
  state: { type: Object as PropType<EditorState>, required: true },
  canUploadImage: { type: Boolean, default: false },
});

// --- Mark name compatibility ---------------------------------------------
// prosemirror-schema-basic uses `strong` / `em`; some schemas alias them to
// `bold` / `italic`. Detect which names this schema uses so the buttons work
// either way without us editing two places.
const boldOrStrongName = (pmSchema.marks.strong ? 'strong' : 'bold');
const emOrItalicName = (pmSchema.marks.em ? 'em' : 'italic');

function markType(name: string): MarkType | null {
  return pmSchema.marks[name] || null;
}
function nodeType(name: string): NodeType | null {
  return pmSchema.nodes[name] || null;
}

// --- Active-state helpers ------------------------------------------------
function isMarkActive(name: string): boolean {
  const mt = markType(name);
  if (!mt) return false;
  const { from, $from, to, empty } = props.state.selection;
  if (empty) return !!mt.isInSet(props.state.storedMarks || $from.marks());
  return props.state.doc.rangeHasMark(from, to, mt);
}

function isBlockActive(name: string, attrs: Record<string, unknown> = {}): boolean {
  const nt = nodeType(name);
  if (!nt) return false;
  const { $from, to } = props.state.selection;
  if (to > $from.end()) return false;
  const parent = $from.parent;
  if (parent.type !== nt) return false;
  return Object.keys(attrs).every((k) => parent.attrs[k] === attrs[k]);
}

function isInList(name: string): boolean {
  const nt = nodeType(name);
  if (!nt) return false;
  const { $from } = props.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type === nt) return true;
  }
  return false;
}

/**
 * True when ANY ancestor (not just the immediate parent) is `name`. Required
 * for wrappers like `blockquote` whose direct child is the paragraph the
 * cursor sits in — `isBlockActive` would always say "no" for those.
 */
function isInBlock(name: string): boolean {
  const nt = nodeType(name);
  if (!nt) return false;
  const { $from } = props.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type === nt) return true;
  }
  return false;
}

// --- Block format dropdown ----------------------------------------------
const blockOptions = [
  { value: 'p',  label: 'Normal',     previewClass: 'pm-toolbar__opt-normal' },
  { value: 'h1', label: 'Heading 1',  previewClass: 'pm-toolbar__opt-h1' },
  { value: 'h2', label: 'Heading 2',  previewClass: 'pm-toolbar__opt-h2' },
  { value: 'h3', label: 'Heading 3',  previewClass: 'pm-toolbar__opt-h3' },
  { value: 'quote', label: 'Quote',   previewClass: 'pm-toolbar__opt-quote' },
  { value: 'code', label: 'Code block', previewClass: 'pm-toolbar__opt-code' },
];

const currentBlockValue = computed(() => {
  // Heading + code_block contain the inline text directly, so $from.parent
  // IS the heading/code_block — isBlockActive works for these.
  if (isBlockActive('heading', { level: 1 })) return 'h1';
  if (isBlockActive('heading', { level: 2 })) return 'h2';
  if (isBlockActive('heading', { level: 3 })) return 'h3';
  if (isBlockActive('code_block')) return 'code';
  // Blockquote wraps a paragraph, so the immediate parent is the paragraph;
  // need to look up the ancestor chain instead.
  if (isInBlock('blockquote')) return 'quote';
  return 'p';
});

const currentBlockLabel = computed(() => {
  const o = blockOptions.find((b) => b.value === currentBlockValue.value);
  return o ? o.label : 'Normal';
});

const blockMenuOpen = ref(false);
const blockBtnEl = ref<HTMLElement | null>(null);
const blockMenuStyle = ref<{ top: string; left: string; minWidth: string }>({
  top: '0px', left: '0px', minWidth: '160px',
});

function toggleBlockMenu() {
  if (!blockMenuOpen.value) {
    const r = blockBtnEl.value?.getBoundingClientRect();
    if (r) {
      blockMenuStyle.value = {
        top: `${Math.round(r.bottom + 4)}px`,
        left: `${Math.round(r.left)}px`,
        minWidth: `${Math.max(160, r.width)}px`,
      };
    }
  }
  blockMenuOpen.value = !blockMenuOpen.value;
}

// Close on outside click / ESC
function onWinDown(e: MouseEvent) {
  if (!blockMenuOpen.value) return;
  // The menu has @mousedown.stop so this only fires for outside clicks.
  const inBtn = blockBtnEl.value?.contains(e.target as Node);
  if (!inBtn) blockMenuOpen.value = false;
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') blockMenuOpen.value = false;
}
if (typeof window !== 'undefined') {
  window.addEventListener('mousedown', onWinDown);
  window.addEventListener('keydown', onKey);
}

function applyBlock(value: string) {
  const v = props.view;
  const dispatch = (tr: Transaction) => v.dispatch(tr);
  if (value === 'p') {
    // If the cursor is inside a blockquote, picking "Normal" should exit the
    // quote, not just re-type the inner block as a paragraph. Lift first,
    // then setBlockType for any non-paragraph block type.
    if (isInBlock('blockquote')) lift(v.state, dispatch);
    const nt = nodeType('paragraph');
    if (nt) setBlockType(nt)(v.state, dispatch);
  } else if (value === 'h1' || value === 'h2' || value === 'h3') {
    const level = Number(value[1]);
    const nt = nodeType('heading');
    if (nt) setBlockType(nt, { level })(v.state, dispatch);
  } else if (value === 'quote') {
    // If already in a quote, treat as toggle-off (lift out). Otherwise wrap.
    if (isInBlock('blockquote')) {
      lift(v.state, dispatch);
    } else {
      const nt = nodeType('blockquote');
      if (nt) wrapIn(nt)(v.state, dispatch);
    }
  } else if (value === 'code') {
    const nt = nodeType('code_block');
    if (nt) setBlockType(nt)(v.state, dispatch);
  }
  v.focus();
}

// --- Commands -----------------------------------------------------------
function toggleMarkByName(name: string) {
  const mt = markType(name);
  if (!mt) return;
  const v = props.view;
  toggleMark(mt)(v.state, (tr) => v.dispatch(tr));
  v.focus();
}

function wrapList(name: string) {
  const nt = nodeType(name);
  if (!nt) return;
  const v = props.view;
  wrapInList(nt)(v.state, (tr) => v.dispatch(tr));
  v.focus();
}

function indent() {
  const v = props.view;
  // Try task_item first (it's our custom node), then list_item.
  const ti = nodeType('task_item');
  const li = nodeType('list_item');
  if (ti && sinkListItem(ti)(v.state, (tr) => v.dispatch(tr))) { v.focus(); return; }
  if (li) sinkListItem(li)(v.state, (tr) => v.dispatch(tr));
  v.focus();
}
function outdent() {
  const v = props.view;
  const ti = nodeType('task_item');
  const li = nodeType('list_item');
  if (ti && liftListItem(ti)(v.state, (tr) => v.dispatch(tr))) { v.focus(); return; }
  if (li) liftListItem(li)(v.state, (tr) => v.dispatch(tr));
  v.focus();
}

function insertTable() {
  const v = props.view;
  const tNode = nodeType('table');
  const trNode = nodeType('table_row');
  const tcNode = nodeType('table_cell');
  if (!tNode || !trNode || !tcNode) return;
  const cell = tcNode.createAndFill();
  if (!cell) return;
  const row = trNode.create(null, [cell, cell, cell]);
  const table = tNode.create(null, [row, row, row]);
  v.dispatch(v.state.tr.replaceSelectionWith(table).scrollIntoView());
  v.focus();
}

function insertDivider() {
  const v = props.view;
  const hr = nodeType('horizontal_rule');
  if (!hr) return;
  v.dispatch(v.state.tr.replaceSelectionWith(hr.create()).scrollIntoView());
  v.focus();
}

function insertImage() {
  const v = props.view;
  const url = window.prompt('Image URL');
  if (!url) return;
  const img = nodeType('image');
  if (!img) return;
  v.dispatch(v.state.tr.replaceSelectionWith(img.create({ src: url })).scrollIntoView());
  v.focus();
}

function setLink() {
  const v = props.view;
  const mt = markType('link');
  if (!mt) return;
  if (isMarkActive('link')) {
    // Toggle off if already a link.
    toggleMark(mt)(v.state, (tr) => v.dispatch(tr));
    v.focus();
    return;
  }
  const href = window.prompt('Link URL');
  if (!href) return;
  const { from, to, empty } = v.state.selection;
  if (empty) {
    // No selection — insert the URL as both text and link.
    const node = v.state.schema.text(href, [mt.create({ href })]);
    v.dispatch(v.state.tr.replaceSelectionWith(node, false));
  } else {
    v.dispatch(v.state.tr.addMark(from, to, mt.create({ href })));
  }
  v.focus();
}

function doUndo() {
  const v = props.view;
  undo(v.state, (tr) => v.dispatch(tr));
  v.focus();
}
function doRedo() {
  const v = props.view;
  redo(v.state, (tr) => v.dispatch(tr));
  v.focus();
}
const canUndo = computed(() => undoDepth(props.state) > 0);
const canRedo = computed(() => redoDepth(props.state) > 0);

function doPrint() {
  // The print stylesheet (see ProseMirrorEditor.vue) is unconditional — any
  // print path (this button, Cmd/Ctrl+P, browser menu) shows only the
  // element marked `.pm-print-root`. We just open the dialog.
  window.print();
}

// Suppress an unused-warning if the import set isn't fully consumed by the
// runtime path (Selection/TextSelection are imported for type narrowing only).
void Selection; void TextSelection;
</script>

<style scoped>
.pm-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
  padding: 6px 8px;
  background: #ffffff;
  border-bottom: 1px solid hsl(220 14% 92%);
  border-radius: 6px 6px 0 0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.pm-toolbar__block {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  font-size: 13px;
  color: hsl(220 9% 30%);
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.pm-toolbar__block:hover { background: hsl(220 14% 96%); }
.pm-toolbar__chev { font-size: 10px; opacity: 0.7; }

.pm-toolbar__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: hsl(220 9% 30%);
  cursor: pointer;
  font-size: 14px;
}
.pm-toolbar__btn:hover { background: hsl(220 14% 96%); }
.pm-toolbar__btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.pm-toolbar__btn:disabled:hover { background: transparent; }
.pm-toolbar__btn.is-active {
  background: hsl(220 14% 92%);
  color: hsl(220 14% 14%);
}

.pm-toolbar__label { font-weight: 600; line-height: 1; font-size: 14px; }
.pm-toolbar__label--u { text-decoration: underline; text-underline-offset: 2px; }
.pm-toolbar__ol { font-weight: 600; font-size: 12px; }

.pm-toolbar__sep {
  width: 1px;
  height: 20px;
  background: hsl(220 14% 90%);
  margin: 0 6px;
}
.pm-toolbar__flex { flex: 1 1 auto; }

/* Block dropdown menu (Teleported to body) */
.pm-toolbar__menu {
  position: fixed;
  background: #ffffff;
  border: 1px solid hsl(220 14% 90%);
  border-radius: 6px;
  box-shadow: 0 6px 24px hsl(220 14% 14% / 0.08);
  padding: 4px;
  z-index: 100;
  min-width: 160px;
}
.pm-toolbar__menu-item {
  display: block;
  width: 100%;
  padding: 6px 10px;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: hsl(220 9% 30%);
}
.pm-toolbar__menu-item:hover { background: hsl(220 14% 96%); }
.pm-toolbar__menu-item.is-active {
  background: hsl(150 30% 95%);
  color: #2B5740;
  font-weight: 600;
}

/* Render the preview text in each option roughly like the block it represents */
.pm-toolbar__opt-normal { font-size: 13px; }
.pm-toolbar__opt-h1 { font-size: 18px; font-weight: 700; }
.pm-toolbar__opt-h2 { font-size: 15px; font-weight: 700; }
.pm-toolbar__opt-h3 { font-size: 13px; font-weight: 600; }
.pm-toolbar__opt-quote { font-style: italic; color: hsl(220 9% 45%); }
.pm-toolbar__opt-code { font-family: ui-monospace, monospace; font-size: 12px; }
</style>

